import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AlertOctagon, Clock, Calendar, Layers, BarChart2, Activity, CheckCircle2, ClipboardList, CalendarClock } from 'lucide-react';
import { insightsService, AuditInsightsResponse } from '../../services/auditService';
import { extractApiError } from '../../utils/apiError';
import Toast, { ToastMessage } from '../common/Toast';
import { AuditInsightsSkeleton } from '../common/InsightsSkeleton';
import NoDataFound from '../common/NoDataFound';
import { usePermission } from '../../contexts/PermissionContext';

// 4-phase lifecycle rollup for the program distribution (matches backend
// _PROGRAM_PHASE_OF: 13 raw workflow_state values → 4 readable phases).
const PROGRAM_PHASES = [
    { key: 'Planning',  label: 'Planning',  color: '#94a3b8' },
    { key: 'Active',    label: 'Active',    color: '#3b82f6' },
    { key: 'In Review', label: 'In Review', color: '#8b5cf6' },
    { key: 'Closed',    label: 'Closed',    color: '#10b981' },
];

// Procedure pipeline states (7 valid values) → colours for the widget.
const PROC_STATE_COLOR: Record<string, string> = {
    Draft: '#94a3b8', Submitted: '#3b82f6', InReview: '#8b5cf6',
    InProgress: '#06b6d4', Approved: '#10b981', Rejected: '#ef4444', Reopened: '#f59e0b',
};

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor, onClick }: any) => (
    <div className={`relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 overflow-hidden ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
        style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
        {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
        {onClick && <span className="absolute top-2.5 right-3 text-[9px] font-bold uppercase tracking-wider text-slate-300">View →</span>}
    </div>
);

/* Section header — separates Programs & Execution vs Findings groups. */
const SectionHeader = ({ icon: Icon, label, accent, hint }: { icon: any; label: string; accent: string; hint?: string }) => (
    <div className="flex items-center gap-2 pt-1">
        <div className="p-1.5 rounded-lg" style={{ background: `${accent}14` }}>
            <Icon size={13} style={{ color: accent }} />
        </div>
        <span className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>{label}</span>
        {hint && <span className="text-[10px] text-slate-400 truncate">— {hint}</span>}
    </div>
);

/* Generic distribution widget (label · bar · count/pct). */
interface DistItem { label: string; count: number; color: string }
const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1' }: {
    title: string; data: DistItem[]; icon: any; total: number; accentColor?: string;
}) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    return (
        <div className="rounded-2xl overflow-hidden flex flex-col flex-1"
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6 italic">No data yet.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1">
                    {data.map((item, idx) => {
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${item.color}bb, ${item.color})`, boxShadow: barPct > 10 ? `0 1px 4px ${item.color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? item.color : '#94a3b8' }}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const AuditInsights: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission('ADT_INSIGHTS', 'view');
    const [data, setData] = useState<AuditInsightsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [, setSearchParams] = useSearchParams();

    // Click-through: jump to a sibling audit tab via the ?tab= URL param
    // (AuditView.tsx reads ?tab= and switches the active tab).
    const goToTab = (tab: string) => setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
    }, { replace: false });

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        (async () => {
            try {
                const d = await insightsService.get();
                if (!cancelled) setData(d);
            } catch (e: any) {
                if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load insights.') });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [canView]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to 'ADT_INSIGHTS'." />
            </div>
        );
    }

    const phaseData = data ? PROGRAM_PHASES.map(p => ({ label: p.label, count: data.programs_by_phase?.[p.key] ?? 0, color: p.color })) : [];
    const procData = data ? Object.entries(data.procedures_by_state || {}).map(([k, v]) => ({ label: k, count: v, color: PROC_STATE_COLOR[k] || '#6366f1' })).filter(d => d.count > 0) : [];
    const findingsSeverity = data ? [
        { label: 'Material', count: data.open_findings.material, color: '#ef4444' },
        { label: 'Trivial', count: data.open_findings.trivial, color: '#f59e0b' },
    ].filter(d => d.count > 0) : [];
    const findingsStatusData = data ? [
        { label: 'Open', count: data.findings_by_status?.open ?? 0, color: '#f59e0b' },
        { label: 'Closed', count: data.findings_by_status?.closed ?? 0, color: '#10b981' },
    ].filter(d => d.count > 0) : [];

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <AuditInsightsSkeleton />}
                    {!loading && data && (
                        <div className="py-4 space-y-5">
                            {/* ── Programs & Execution ─────────────────────── */}
                            <SectionHeader icon={Layers} label="Programs & Execution" accent="#6366f1"
                                hint="Audit programs and their procedure execution pipeline." />
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Total Programs" value={data.total_programs}
                                    sub={`${data.programs_by_phase?.['Active'] ?? 0} active · ${data.programs_by_phase?.['Closed'] ?? 0} closed`}
                                    icon={Layers} iconBg="#f5f3ff" iconColor="#8b5cf6" onClick={() => goToTab('Programs')} />
                                <KpiCard label="Due This Week" value={data.due_this_week}
                                    sub="Procedures scheduled this week"
                                    icon={Calendar} iconBg="#eff6ff" iconColor="#3b82f6" onClick={() => goToTab('Schedule')} />
                                <KpiCard label="Overdue" value={data.overdue}
                                    sub="Procedures past scheduled date"
                                    icon={Clock}
                                    iconBg={data.overdue > 0 ? '#fef2f2' : '#f8fafc'}
                                    iconColor={data.overdue > 0 ? '#ef4444' : '#94a3b8'} onClick={() => goToTab('Procedures')} />
                                <KpiCard label="Upcoming (30 days)" value={data.upcoming_30d}
                                    sub="Procedures starting soon"
                                    icon={CalendarClock} iconBg="#ecfdf5" iconColor="#10b981" onClick={() => goToTab('Schedule')} />
                            </div>
                            <div className="flex gap-4 items-stretch">
                                <DistributionWidget title="Programs by Phase" data={phaseData} icon={BarChart2} total={data.total_programs} accentColor="#6366f1" />
                                <DistributionWidget title="Procedures Progress" data={procData} icon={ClipboardList} total={procData.reduce((s, d) => s + d.count, 0)} accentColor="#06b6d4" />
                            </div>

                            {/* ── Findings ─────────────────────────────────── */}
                            <SectionHeader icon={AlertOctagon} label="Findings" accent="#f59e0b"
                                hint="Issues raised during audit execution." />
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Open Findings" value={data.open_findings.total}
                                    sub={`${data.open_findings.material} material · ${data.open_findings.trivial} trivial`}
                                    icon={AlertOctagon} iconBg="#fffbeb" iconColor="#f59e0b" onClick={() => goToTab('Findings')} />
                                <KpiCard label="Closed Findings" value={data.findings_by_status?.closed ?? 0}
                                    sub={`of ${data.findings_by_status?.total ?? 0} total`}
                                    icon={CheckCircle2} iconBg="#ecfdf5" iconColor="#10b981" onClick={() => goToTab('Findings')} />
                            </div>
                            <div className="flex gap-4 items-stretch">
                                <DistributionWidget title="Findings by Severity" data={findingsSeverity} icon={AlertOctagon} total={findingsSeverity.reduce((s, d) => s + d.count, 0)} accentColor="#ef4444" />
                                <DistributionWidget title="Findings by Status" data={findingsStatusData} icon={Activity} total={data.findings_by_status?.total ?? 0} accentColor="#10b981" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default AuditInsights;
